import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Apps as AppsIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { companyConfigService } from '@/services/companyConfigService';
import { 
  CompanyConfiguration, 
  CreateCompanyConfigurationRequest,
  UpdateCompanyConfigurationRequest,
  CompanyConfigurationHistoryResponse
} from '@/types/company-config';
import CompanyInfoTab from '@/components/company-config/CompanyInfoTab';
import ContactInfoTab from '@/components/company-config/ContactInfoTab';
import PlatformSettingsTab from '@/components/company-config/PlatformSettingsTab';
import SecuritySettingsTab from '@/components/company-config/SecuritySettingsTab';
import FeaturesTab from '@/components/company-config/FeaturesTab';
import BrandingTab from '@/components/company-config/BrandingTab';
import AppConfigurationsTab from '@/components/company-config/AppConfigurationsTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-config-tabpanel-${index}`}
      aria-labelledby={`company-config-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CompanyConfigurationPage: React.FC = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [configuration, setConfiguration] = useState<CompanyConfiguration | null>(null);
  const [formData, setFormData] = useState<Partial<CompanyConfiguration>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<CompanyConfigurationHistoryResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const config = await companyConfigService.getCompanyConfiguration();
      setConfiguration(config);
      setFormData(config);
      setHasChanges(false);
    } catch (err: unknown) {
      const error = err as { response?: { status?: number } };
      if (error.response?.status === 404) {
        // No configuration exists yet
        setConfiguration(null);
        setFormData({
          companyInfo: { name: '', legalName: '' },
          contactInfo: { email: '' }
        });
      } else {
        setError('Failed to load company configuration');
        console.error('Error loading configuration:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const historyData = await companyConfigService.getCompanyConfigurationHistory();
      setHistory(historyData);
    } catch (err) {
      setError('Failed to load configuration history');
      console.error('Error loading history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleFieldChange = (section: string, field: string, value: unknown) => {
    if (!isAdmin) return; // Prevent changes if not admin

    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section as keyof typeof prev] as Record<string, unknown> || {}),
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!isAdmin) return;

    try {
      setSaving(true);
      setError(null);

      // Validate required fields
      const companyErrors = formData.companyInfo 
        ? companyConfigService.validateCompanyInfo(formData.companyInfo)
        : ['Company information is required'];
      const contactErrors = formData.contactInfo
        ? companyConfigService.validateContactInfo(formData.contactInfo)  
        : ['Contact information is required'];
      const allErrors = [...companyErrors, ...contactErrors];

      if (allErrors.length > 0) {
        setError(`Validation errors: ${allErrors.join(', ')}`);
        return;
      }

      let savedConfig: CompanyConfiguration;

      if (configuration) {
        // Update existing configuration
        const updateData: UpdateCompanyConfigurationRequest = {
          companyInfo: formData.companyInfo,
          contactInfo: formData.contactInfo,
          platformSettings: formData.platformSettings,
          securitySettings: formData.securitySettings,
          features: formData.features,
          appConfigurations: formData.appConfigurations,
          branding: formData.branding
        };
        savedConfig = await companyConfigService.updateCompanyConfiguration(configuration.id, updateData);
      } else {
        // Create new configuration
        const createData: CreateCompanyConfigurationRequest = {
          companyInfo: formData.companyInfo!,
          contactInfo: formData.contactInfo!,
          platformSettings: formData.platformSettings,
          securitySettings: formData.securitySettings,
          features: formData.features,
          appConfigurations: formData.appConfigurations,
          branding: formData.branding
        };
        savedConfig = await companyConfigService.createCompanyConfiguration(createData);
      }

      setConfiguration(savedConfig);
      setFormData(savedConfig);
      setHasChanges(false);
      setSuccess(configuration ? 'Configuration updated successfully!' : 'Configuration created successfully!');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to save configuration');
      console.error('Error saving configuration:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (configuration) {
      setFormData(configuration);
    } else {
      setFormData({
        companyInfo: { name: '', legalName: '' },
        contactInfo: { email: '' }
      });
    }
    setHasChanges(false);
  };

  const handleViewHistory = () => {
    setHistoryOpen(true);
    loadHistory();
  };

  const tabs = [
    { label: 'Company Info', icon: <BusinessIcon /> },
    { label: 'Contact Info', icon: <BusinessIcon /> },
    { label: 'Platform Settings', icon: <SettingsIcon /> },
    { label: 'Security', icon: <SecurityIcon /> },
    { label: 'Features', icon: <AppsIcon /> },
    { label: 'Apps Config', icon: <AppsIcon /> },
    { label: 'Branding', icon: <PaletteIcon /> }
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Company Configuration
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={handleViewHistory}
            disabled={!isAdmin}
          >
            History
          </Button>
          {isAdmin && hasChanges && (
            <>
              <Button
                variant="outlined"
                startIcon={<ResetIcon />}
                onClick={handleReset}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have read-only access to company configuration. Only administrators can make changes.
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                id={`company-config-tab-${index}`}
                aria-controls={`company-config-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <CompanyInfoTab
            data={formData.companyInfo || { name: '', legalName: '' }}
            onChange={(field, value) => handleFieldChange('companyInfo', field, value)}
            readOnly={!isAdmin}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <ContactInfoTab
            data={formData.contactInfo || { email: '' }}
            onChange={(field, value) => handleFieldChange('contactInfo', field, value)}
            readOnly={!isAdmin}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <PlatformSettingsTab
            data={formData.platformSettings || {}}
            onChange={(field, value) => handleFieldChange('platformSettings', field, value)}
            readOnly={!isAdmin}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <SecuritySettingsTab
            data={formData.securitySettings || {}}
            onChange={(field, value) => handleFieldChange('securitySettings', field, value)}
            readOnly={!isAdmin}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <FeaturesTab
            data={formData.features || {}}
            onChange={(field, value) => handleFieldChange('features', field, value)}
            readOnly={!isAdmin}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={5}>
          <AppConfigurationsTab
            data={formData.appConfigurations || {}}
            onChange={(field, value) => handleFieldChange('appConfigurations', field, value)}
            readOnly={!isAdmin}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={6}>
          <BrandingTab
            data={formData.branding || {}}
            onChange={(field, value) => handleFieldChange('branding', field, value)}
            readOnly={!isAdmin}
          />
        </TabPanel>
      </Card>

      {/* History Dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Configuration History</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : history ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Version</TableCell>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Updated By</TableCell>
                    <TableCell>Updated At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.configurations.map((config, index) => (
                    <TableRow key={config.id}>
                      <TableCell>v{history.configurations.length - index}</TableCell>
                      <TableCell>{config.companyInfo.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={config.isActive ? 'Active' : 'Inactive'}
                          color={config.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{config.updatedBy || config.createdBy}</TableCell>
                      <TableCell>
                        {new Date(config.updatedAt).toLocaleDateString()} {new Date(config.updatedAt).toLocaleTimeString()}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Configuration">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {isAdmin && !config.isActive && (
                          <Tooltip title="Delete Configuration">
                            <IconButton size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No configuration history found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CompanyConfigurationPage;